import { useEffect, useRef, useState } from 'react';

import { IconChevronDown, IconChevronLeft, IconChevronRight } from './icons';

interface IncidentsPaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const LIMIT_OPTIONS = [10, 20, 50];

export const IncidentsPagination = ({ page, limit, total, onPageChange, onLimitChange }: IncidentsPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4 text-sm text-zinc-500">
        <LimitDropdown value={limit} onChange={onLimitChange} />
        <span className="font-mono tabular-nums">
          {total === 0 ? '0' : `${start}–${end}`} sur {total}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <PageButton onClick={() => onPageChange(page - 1)} disabled={prevDisabled}>
          <IconChevronLeft width={14} height={14} />
          <span>Précédent</span>
        </PageButton>
        <PageButton onClick={() => onPageChange(page + 1)} disabled={nextDisabled}>
          <span>Suivant</span>
          <IconChevronRight width={14} height={14} />
        </PageButton>
      </div>
    </div>
  );
};

const PageButton = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 h-8 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 hover:border-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-zinc-200 transition-colors duration-100"
  >
    {children}
  </button>
);

const LimitDropdown = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 h-8 px-2.5 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-100"
      >
        <span className="font-mono tabular-nums">{value}</span>
        <span className="text-zinc-500">par page</span>
        <IconChevronDown width={12} height={12} className="text-zinc-400" />
      </button>
      {open ? (
        <div className="absolute bottom-full mb-1.5 left-0 z-50 w-32 bg-white border border-zinc-200 rounded-md shadow-md py-1">
          {LIMIT_OPTIONS.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={[
                'w-full h-8 px-3 text-sm flex items-center justify-between rounded transition-colors',
                opt === value ? 'text-zinc-900 font-medium bg-zinc-50' : 'text-zinc-700 hover:bg-zinc-50',
              ].join(' ')}
            >
              <span className="font-mono tabular-nums">{opt}</span>
              <span className="text-zinc-500 text-xs">par page</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
